import type { ReactNode } from 'react';
import { goldGradient, tokens } from './tokens';

type Props = Readonly<{
  children: ReactNode;
  loading?: boolean;
  disabled?: boolean;
  loadingLabel?: string;
  /** Render a 600 ms green "Submitted!" confirmation before the parent
   *  transitions to the next step. The parent controls the flag and clears
   *  it (or unmounts) after the delay. */
  success?: boolean;
  successLabel?: string;
}>;

/**
 * Opinionated full-width submit button. Always type="submit", always uses
 * the QMI gold gradient when active, muted gray when loading/disabled, and
 * a green confirmation flash when success=true.
 */
export function FormSubmitButton({
  children,
  loading = false,
  disabled = false,
  loadingLabel = 'Sending...',
  success = false,
  successLabel = '✓ Submitted!',
}: Props): JSX.Element {
  const isInactive = loading || disabled || success;

  let background: string = goldGradient;
  let color: string = tokens.navy;
  if (success) {
    background = tokens.successBg;
    color = tokens.successText;
  } else if (isInactive) {
    background = tokens.disabledBg;
    color = tokens.disabledText;
  }

  let label: ReactNode = children;
  if (success) label = successLabel;
  else if (loading) label = loadingLabel;

  return (
    <button
      type="submit"
      disabled={isInactive}
      className="w-full py-3 rounded-xl font-semibold text-sm transition-all"
      style={{
        background,
        color,
        cursor: isInactive ? 'not-allowed' : 'pointer',
        border: success ? `1px solid ${tokens.successBorder}` : 'none',
      }}
    >
      {label}
    </button>
  );
}
